import * as React from "react";
import { useState, useEffect, useMemo, useCallback } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Paper from "@mui/material/Paper";
import Snackbar from "@mui/material/Snackbar";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { apiEndpoint } from "../config/api";
import { apiFetch } from "../services/api";
import { rollupFranchises, summarize } from "../utils/eligibilityRollup";
import EligibilityFranchiseRow from "./EligibilityFranchiseRow";
import EligibilityAddPeriodDialog from "./EligibilityAddPeriodDialog";

// Eligibility Administration — the permanent surface for the LK/SI Audit eligibility configuration
// (design §5). Franchise-grain triage list (rolled up from the period-grain read) + expandable
// per-franchise timeline. This slice adds the first authenticated write action, Reject, to active
// proposals; a successful write reloads the data and shows a success message.
//
// Deliberately standalone — NOT nested inside the ComCodes franchise rows, which are gated on
// isLilKickerClient = 1 and would hide the former franchises this surface exists for.
export default function EligibilityAdministration() {
  const [periods, setPeriods] = useState([]);
  const [error, setError] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [query, setQuery] = useState("");
  const [message, setMessage] = useState(null);
  const [addOpen, setAddOpen] = useState(false);

  const load = useCallback(() => {
    return apiFetch(`${apiEndpoint}/franchise-eligibility-periods`)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data) => {
        setPeriods(data.periods || []);
        setError(false);
        setLoaded(true);
      })
      .catch((e) => {
        console.log("franchise-eligibility-periods failed:", e);
        setError(true);
        setLoaded(true);
      });
  }, []);

  useEffect(() => { load(); }, [load]);

  // Called by a child after a successful write: surface a message and reload the authoritative data.
  const handleChanged = useCallback((msg) => {
    if (msg) setMessage(msg);
    load();
  }, [load]);

  const franchises = useMemo(() => rollupFranchises(periods), [periods]);
  const summary = useMemo(() => summarize(franchises), [franchises]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return franchises;
    return franchises.filter(
      (f) =>
        String(f.com_code).toLowerCase().includes(q) ||
        String(f.franchise_name || "").toLowerCase().includes(q)
    );
  }, [franchises, query]);

  const summaryChips = [
    { label: "Franchises", value: summary.total },
    { label: "Confirmed", value: summary.confirmed },
    { label: "Needs review", value: summary.needsReview },
    { label: "Fail-open", value: summary.failOpen },
  ].map((s) => (
    <Chip key={s.label} size="small" variant="outlined" label={`${s.label}: ${s.value}`} />
  ));

  const rows = filtered.map((f) => (
    <EligibilityFranchiseRow key={f.com_code} franchise={f} onChanged={handleChanged} />
  ));

  // A7: the outer <Accordion> was dropped when this module moved into its own tab -- a tab that
  // exists to show this workflow should not require a second click to reveal it. Nothing else in
  // the module changed.
  return (
    <Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Controls which warehouse operational seasons the Season Audit pulls for each franchise.
          A franchise with no configuration <strong>fails open</strong> — it keeps the current{" "}
          <em>LK/SI Client</em> behaviour. One row per franchise; expand a row for its eligibility
          timeline. Active proposals can be reviewed &amp; confirmed, adjusted, or rejected; confirmed
          periods can be edited.
        </Typography>

        <Box sx={{ mb: 1.5 }}>
          <Button variant="outlined" size="small" onClick={() => setAddOpen(true)}>
            + Add Eligibility Period
          </Button>
        </Box>

        {error ? (
          <Typography variant="body2" color="error">
            Unable to load eligibility periods. Please try again later.
          </Typography>
        ) : !loaded ? (
          <Typography variant="body2" color="text.secondary">Loading…</Typography>
        ) : franchises.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No eligibility records to display.</Typography>
        ) : (
          <>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1.5, alignItems: "center" }}>
              {summaryChips}
              <Box sx={{ flexGrow: 1 }} />
              <TextField
                size="small"
                placeholder="Filter by com code or name"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                sx={{ minWidth: 240 }}
              />
            </Box>
            <Paper variant="outlined" sx={{ overflowX: "auto" }}>
              <Table size="small" sx={{ minWidth: 900 }}>
                <TableHead>
                  <TableRow>
                    <TableCell />
                    <TableCell>Com Code</TableCell>
                    <TableCell>Franchise Name</TableCell>
                    <TableCell align="center">LK/SI Client</TableCell>
                    <TableCell>Configuration</TableCell>
                    <TableCell>Attention</TableCell>
                    <TableCell>Periods</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>{rows}</TableBody>
              </Table>
            </Paper>
            {filtered.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                No franchises match “{query}”.
              </Typography>
            )}
          </>
        )}

        <EligibilityAddPeriodDialog
          open={addOpen}
          onClose={() => setAddOpen(false)}
          onCreated={(msg) => { setAddOpen(false); handleChanged(msg); }}
        />

        <Snackbar
          open={message !== null}
          autoHideDuration={4000}
          onClose={() => setMessage(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert severity="success" onClose={() => setMessage(null)} sx={{ width: "100%" }}>
            {message}
          </Alert>
        </Snackbar>
    </Box>
  );
}
